import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { of, filter, switchMap, combineLatest } from 'rxjs';

import { IPushBanner, SdPushBannerComponent } from '@seb/web-design-system/push-banner';
import { BrandThemesEnum, StatusEnum, ThemesEnum } from '@seb/web-design-system/core';

import { ComponentService, HobStateService, ToasterService, TrackingService } from '../../../services';
import { PromoBannerBackgroundThemeEnum, PromoBannerTagThemeEnum } from '../../../enums';
import { AbstractComponent } from '../abstract-component.component';
import { PromoBannerComponentInterface } from '../../../models';
import { LinkMapper } from '../../../mappers';

@Component({
  selector: 'wc-promo-banner',
  templateUrl: './promo-banner.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SdPushBannerComponent]
})
export class PromoBannerComponent extends AbstractComponent<PromoBannerComponentInterface> implements OnInit {
  pushBannerData: IPushBanner = {};
  themesConfig: ThemesEnum | BrandThemesEnum.brand;
  isCopyTag = true;

  private uid: string;
  private name: string;

  constructor(
    private cdRef: ChangeDetectorRef,
    private componentService: ComponentService,
    private trackingService: TrackingService,
    private toasterService: ToasterService,
    protected readonly hobStateService: HobStateService
  ) {
    super();
  }

  override mandatoryFunction = (cmp: PromoBannerComponentInterface): boolean => {
    return !!cmp.content && !!cmp.backgroundTheme;
  };

  ngOnInit(): void {
    this.subscriptions.add(
      this.component$.pipe(
        switchMap((cmp) => combineLatest([
          this.hobStateService.hobState$,
          cmp.cmsLinks && cmp?.cmsLinks?.length > 0
            ? this.componentService.getLazyComponentData(cmp, 'cmsLinks').pipe(
              filter((cmp): cmp is PromoBannerComponentInterface => !!cmp)
            )
            : of(cmp)
        ]))
      ).subscribe(([{ isHob, liveBrand, siteBrand }, cmp]) => {
        this.themesConfig = isHob
          ? BrandThemesEnum.brand
          : this.mapPromoBannerBackgroundTheme(cmp.backgroundTheme);

        this.brandCorner = liveBrand || siteBrand;

        this.uid = cmp.uid ? cmp.uid : 'promoBanner';
        this.name = cmp.name;
        this.isCopyTag = cmp.copyIcon === 'true';

        this.pushBannerData = {
          title: cmp.content,
          details: cmp.tooltipText,
          links: cmp?.cmsLinksObjects?.map(LinkMapper.cmsLinkToILink)
        };

        if (cmp.tagLabel) {
          this.pushBannerData.tag = {
            text: cmp.tagLabel,
            theme: isHob ? ThemesEnum.light : this.mapPromoBannerTagTheme(cmp.tagTheme)
          };
        }

        this.trackingService.sendEvent('promotionView', {
          promotionID: cmp.uid,
          promotionName: cmp.name,
          promotionPosition: 'push promo banner',
          promotionType: 'promo banner'
        });

        this.setLoaded();
        this.cdRef.markForCheck();
      })
    );
  }

  private mapPromoBannerBackgroundTheme(promoBanner: PromoBannerBackgroundThemeEnum): ThemesEnum {
    switch (promoBanner) {
      case PromoBannerBackgroundThemeEnum.BACKGROUND_LIGHT:
        return ThemesEnum.light;
      case PromoBannerBackgroundThemeEnum.BACKGROUND_DARK:
        return ThemesEnum.dark;
      case PromoBannerBackgroundThemeEnum.BACKGROUND_PROMO1:
        return ThemesEnum.promo;
      case PromoBannerBackgroundThemeEnum.BACKGROUND_PROMO2:
        return ThemesEnum.promo2;
      default:
        return ThemesEnum.default;
    }
  }

  private mapPromoBannerTagTheme(promoBanner: PromoBannerTagThemeEnum): ThemesEnum {
    switch (promoBanner) {
      case PromoBannerTagThemeEnum.TAGTHEME_LIGHT:
        return ThemesEnum.light;
      case PromoBannerTagThemeEnum.TAGTHEME_DARK:
        return ThemesEnum.dark;
      case PromoBannerTagThemeEnum.TAGTHEME_PROMO1:
        return ThemesEnum.promo;
      case PromoBannerTagThemeEnum.TAGTHEME_PROMO2:
        return ThemesEnum.promo2;
      default:
        return ThemesEnum.default;
    }
  }

  trackClickEvent(_event: string) {
    this.trackingService.sendEvent('promotionClick', {
      promotionID: this.uid,
      promotionName: this.name,
      promotionPosition: 'push promo banner',
      promotionType: 'promo banner'
    });
  }

  onTagCopied(_event: string) {
    this.toasterService.sendToasterEvent({
      title: 'promo.toast.title',
      description: 'promo.toast.description',
      status: StatusEnum.success
    });
  }
}
